import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAdmin } from '@/hooks/useAdmin.ts'
import { useWallet } from '@/hooks/useWallet.ts'
import { env } from '@/config/env.ts'
import { findChainById, getExplorerUrl, getChainSlug } from '@/config/chains.ts'
import { cn } from '@/utils/cn.ts'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type Status = 'operational' | 'degraded' | 'outage' | 'unknown'

interface EnvCheck {
  env: string
  label: string
  status: Status
  latencyMs: number
  message?: string
}

interface ServiceResult {
  name: string
  slug: string
  icon: string
  category: string
  status: Status
  statusPageUrl?: string
  checks: EnvCheck[]
}

interface HealthResponse {
  timestamp: string
  overall: Status
  services: ServiceResult[]
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const REFRESH_INTERVAL = 30_000

const STATUS_META: Record<
  Status,
  { label: string; dot: string; bg: string; text: string; border: string }
> = {
  operational: {
    label: 'Operational',
    dot: 'bg-emerald-500',
    bg: 'bg-emerald-500/10',
    text: 'text-emerald-600 dark:text-emerald-400',
    border: 'border-emerald-500/20',
  },
  degraded: {
    label: 'Degraded',
    dot: 'bg-amber-500',
    bg: 'bg-amber-500/10',
    text: 'text-amber-600 dark:text-amber-400',
    border: 'border-amber-500/20',
  },
  outage: {
    label: 'Outage',
    dot: 'bg-red-500',
    bg: 'bg-red-500/10',
    text: 'text-red-600 dark:text-red-400',
    border: 'border-red-500/20',
  },
  unknown: {
    label: 'Unknown',
    dot: 'bg-gray-400',
    bg: 'bg-gray-400/10',
    text: 'text-fg-muted',
    border: 'border-line',
  },
}

const OVERALL_BANNER: Record<
  Status,
  { heading: string; sub: string; cls: string }
> = {
  operational: {
    heading: 'All Systems Operational',
    sub: 'All services are running normally.',
    cls: 'from-emerald-500/15 to-emerald-500/5 border-emerald-500/20',
  },
  degraded: {
    heading: 'Partial System Degradation',
    sub: 'Some services are experiencing issues.',
    cls: 'from-amber-500/15 to-amber-500/5 border-amber-500/20',
  },
  outage: {
    heading: 'System Outage Detected',
    sub: 'One or more critical services are down.',
    cls: 'from-red-500/15 to-red-500/5 border-red-500/20',
  },
  unknown: {
    heading: 'Checking Systems…',
    sub: 'Running health checks.',
    cls: 'from-gray-500/15 to-gray-500/5 border-line',
  },
}

const EXPLORER_URL = getExplorerUrl(env.chainId) || ''
const ACTIVE_CHAIN_NAME =
  findChainById(env.chainId)?.name ?? `Chain ${env.chainId}`

const EXTERNAL_STATUS_PAGES = [
  { name: 'Lit Protocol', url: 'https://status.litprotocol.com' },
  { name: 'Pinata / IPFS', url: 'https://status.pinata.cloud' },
  { name: 'WalletConnect', url: 'https://status.walletconnect.com' },
  { name: 'Supabase', url: 'https://status.supabase.com' },
  { name: 'Resend', url: 'https://resend-status.com' },
  ...(EXPLORER_URL
    ? [{ name: `Explorer (${ACTIVE_CHAIN_NAME})`, url: EXPLORER_URL }]
    : []),
]

/* ------------------------------------------------------------------ */
/*  Data fetching                                                      */
/* ------------------------------------------------------------------ */

async function fetchHealth(): Promise<HealthResponse> {
  const base = env.notificationApiBase?.trim() || ''
  const url = `${base}/api/health`
  const res = await fetch(url, { cache: 'no-store' })
  if (!res.ok) throw new Error(`Health API returned ${res.status}`)
  return (await res.json()) as HealthResponse
}

/* ------------------------------------------------------------------ */
/*  Sub-components                                                     */
/* ------------------------------------------------------------------ */

function StatusDot({
  status,
  size = 'sm',
}: {
  status: Status
  size?: 'sm' | 'lg'
}) {
  const meta = STATUS_META[status]
  const px = size === 'lg' ? 'h-3.5 w-3.5' : 'h-2.5 w-2.5'
  return (
    <span className="relative flex items-center justify-center">
      {status === 'operational' && (
        <span
          className={cn(
            'absolute inline-flex animate-ping rounded-full opacity-40',
            meta.dot,
            px,
          )}
        />
      )}
      <span className={cn('relative inline-flex rounded-full', meta.dot, px)} />
    </span>
  )
}

function OverallBanner({
  data,
  loading,
}: {
  data: HealthResponse | null
  loading: boolean
}) {
  const status = data?.overall ?? 'unknown'
  const banner = OVERALL_BANNER[loading ? 'unknown' : status]
  const meta = STATUS_META[loading ? 'unknown' : status]

  return (
    <div
      className={cn(
        'rounded-xl border bg-gradient-to-br p-6 md:p-8',
        banner.cls,
      )}
    >
      <div className="flex items-center gap-3">
        <StatusDot status={loading ? 'unknown' : status} size="lg" />
        <h2 className={cn('text-xl font-bold md:text-2xl', meta.text)}>
          {banner.heading}
        </h2>
      </div>
      <p className="text-fg-secondary mt-2 text-sm">{banner.sub}</p>
      {data && (
        <p className="text-fg-muted mt-3 text-xs">
          Last checked: {new Date(data.timestamp).toLocaleString()}
        </p>
      )}
    </div>
  )
}

function ServiceCard({ svc }: { svc: ServiceResult }) {
  const meta = STATUS_META[svc.status]
  const showCheckDetails =
    svc.checks.length > 0 &&
    (svc.status === 'outage' ||
      svc.status === 'degraded' ||
      svc.status === 'unknown')

  return (
    <div
      className={cn(
        'rounded-xl border p-5 transition-all duration-200 hover:shadow-sm',
        meta.border,
        meta.bg,
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <span className="text-xl">{svc.icon}</span>
          <h3 className="text-fg font-semibold">{svc.name}</h3>
        </div>
        <StatusDot status={svc.status} />
      </div>
      <p className={cn('mt-2 text-sm font-medium', meta.text)}>{meta.label}</p>

      {svc.statusPageUrl &&
        (svc.status === 'outage' || svc.status === 'degraded') && (
          <a
            href={svc.statusPageUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent mt-2 inline-block text-xs hover:underline"
          >
            Official status page ↗
          </a>
        )}

      {showCheckDetails && (
        <div
          className="border-line bg-surface-muted/40 mt-4 space-y-3 rounded-lg border p-3"
          role="region"
          aria-label={`${svc.name} check details`}
        >
          <p className="text-fg-muted text-xs font-medium tracking-wide uppercase">
            Check details
          </p>
          {svc.checks.map((c) => {
            const cm = STATUS_META[c.status]
            return (
              <div
                key={c.env}
                className="border-line/80 border-t pt-3 first:border-t-0 first:pt-0"
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-fg text-sm font-medium">{c.label}</p>
                    {c.message && (
                      <p className="text-fg-secondary mt-1 font-mono text-xs break-words whitespace-pre-wrap">
                        {c.message}
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <span className={cn('text-xs font-medium', cm.text)}>
                      {cm.label}
                    </span>
                    <LatencyBar ms={c.latencyMs} />
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function LatencyBar({ ms }: { ms: number }) {
  const pct = Math.min(ms / 2000, 1) * 100
  const color =
    ms < 300 ? 'bg-emerald-500' : ms < 1000 ? 'bg-amber-500' : 'bg-red-500'
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
        <div
          className={cn('h-full rounded-full transition-all', color)}
          style={{ width: `${Math.max(pct, 4)}%` }}
        />
      </div>
      <span className="text-fg-muted text-xs tabular-nums">{ms}ms</span>
    </div>
  )
}

function AdminServiceRow({ svc }: { svc: ServiceResult }) {
  const meta = STATUS_META[svc.status]
  return (
    <div className={cn('overflow-hidden rounded-xl border', meta.border)}>
      <div className="bg-surface-raised/50 flex items-center justify-between px-5 py-4">
        <div className="flex items-center gap-2.5">
          <span className="text-lg">{svc.icon}</span>
          <h3 className="text-fg font-semibold">{svc.name}</h3>
          <span
            className={cn(
              'rounded-full px-2 py-0.5 text-xs font-medium',
              meta.bg,
              meta.text,
            )}
          >
            {meta.label}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {svc.statusPageUrl && (
            <a
              href={svc.statusPageUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent text-xs hover:underline"
            >
              Status Page ↗
            </a>
          )}
          <StatusDot status={svc.status} />
        </div>
      </div>

      {svc.checks.length > 0 && (
        <div className="divide-line divide-y">
          {svc.checks.map((c) => {
            const cm = STATUS_META[c.status]
            return (
              <div
                key={c.env}
                className="flex items-center justify-between px-5 py-3 text-sm"
              >
                <div className="flex items-center gap-2.5">
                  <StatusDot status={c.status} />
                  <span className="text-fg font-medium">{c.label}</span>
                </div>
                <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-4">
                  {c.message && (
                    <span className="text-fg-muted max-w-md text-right text-xs break-words whitespace-pre-wrap md:max-w-lg">
                      {c.message}
                    </span>
                  )}
                  <span className={cn('text-xs font-medium', cm.text)}>
                    {cm.label}
                  </span>
                  <LatencyBar ms={c.latencyMs} />
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function Skeleton() {
  return (
    <div className="animate-pulse space-y-4">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="bg-surface-muted h-20 rounded-xl" />
      ))}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export function StatusPage() {
  const { t } = useTranslation()
  const { isOwner } = useAdmin()
  const { isConnected } = useWallet()
  const [data, setData] = useState<HealthResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [adminView, setAdminView] = useState(false)
  const [autoRefresh, setAutoRefresh] = useState(true)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await fetchHealth()
      setData(result)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch health data')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(() => void refresh(), REFRESH_INTERVAL)
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [autoRefresh, refresh])

  const showAdmin = isConnected && isOwner

  return (
    <section className="max-w-content mx-auto px-4 py-8 md:px-8 md:py-12">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-fg text-[1.75rem] font-bold max-md:text-[1.375rem]">
          {t('status.title', 'System Status')}
        </h1>
        <p className="text-fg-secondary mt-1">
          {t('status.subtitle', 'Real-time health of BitsLegacy services')}
        </p>
      </div>

      {/* Overall banner */}
      <OverallBanner data={data} loading={loading && !data} />

      {/* Controls */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => void refresh()}
          disabled={loading}
          className={cn(
            'border-line text-fg hover:bg-surface-muted rounded-lg border px-4 py-2 text-sm font-medium transition-all',
            loading && 'cursor-not-allowed opacity-60',
          )}
        >
          {loading ? 'Checking…' : '↻ Refresh'}
        </button>

        <label className="text-fg-secondary flex cursor-pointer items-center gap-2 text-sm select-none">
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
            className="accent-accent h-4 w-4 rounded"
          />
          Auto-refresh (30s)
        </label>

        {showAdmin && (
          <button
            type="button"
            onClick={() => setAdminView((v) => !v)}
            className={cn(
              'ml-auto rounded-lg px-4 py-2 text-sm font-medium transition-all',
              adminView
                ? 'bg-accent text-fg-inverse'
                : 'border-accent/30 text-accent hover:bg-accent-muted border',
            )}
          >
            {adminView ? '← Simple View' : '⚙ Admin View'}
          </button>
        )}
      </div>

      {error && (
        <div className="mt-4 rounded-lg border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-600 dark:text-red-400">
          {error}
        </div>
      )}

      {/* ---- END-USER VIEW ---- */}
      {!adminView && (
        <div className="mt-8">
          {loading && !data ? (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,240px),1fr))] gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div
                  key={i}
                  className="bg-surface-muted h-28 animate-pulse rounded-xl"
                />
              ))}
            </div>
          ) : data ? (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,240px),1fr))] gap-4">
              {data.services.map((svc) => (
                <ServiceCard key={svc.slug} svc={svc} />
              ))}
            </div>
          ) : null}
        </div>
      )}

      {/* ---- ADMIN VIEW ---- */}
      {adminView && showAdmin && (
        <div className="mt-8 space-y-4">
          {loading && !data ? (
            <Skeleton />
          ) : data ? (
            <>
              {data.services.map((svc) => (
                <AdminServiceRow key={svc.slug} svc={svc} />
              ))}

              {/* External status page links */}
              <div className="border-line mt-10 rounded-xl border p-6">
                <h3 className="text-fg mb-4 font-semibold">
                  External Status Pages
                </h3>
                <p className="text-fg-muted mb-4 text-sm">
                  Visit the official status pages for detailed uptime and
                  incident history.
                </p>
                <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,200px),1fr))] gap-3">
                  {EXTERNAL_STATUS_PAGES.map((sp) => (
                    <a
                      key={sp.url}
                      href={sp.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="border-line text-fg hover:bg-surface-muted hover:border-accent/30 flex items-center gap-2 rounded-lg border px-4 py-3 text-sm font-medium transition-all"
                    >
                      <span className="text-accent">↗</span>
                      {sp.name}
                    </a>
                  ))}
                </div>
              </div>

              {/* Environment info (admin only) */}
              <div className="border-line mt-6 rounded-xl border p-6">
                <h3 className="text-fg mb-4 font-semibold">
                  Environment Configuration
                </h3>
                <div className="grid gap-3 text-sm">
                  <EnvRow
                    label="App Environment"
                    value={env.appEnv || 'unknown'}
                  />
                  <EnvRow
                    label="Contract Network"
                    value={`${ACTIVE_CHAIN_NAME} (${env.chainId}) — ${getChainSlug(env.chainId)}`}
                  />
                  <EnvRow
                    label="Contract Address"
                    value={
                      env.contractAddress
                        ? `${env.contractAddress.slice(0, 10)}…${env.contractAddress.slice(-6)}`
                        : 'not set'
                    }
                  />
                  <EnvRow label="Lit backend" value="Chipotle" />
                  <EnvRow
                    label="Supabase"
                    value={
                      env.supabaseUrl
                        ? new URL(env.supabaseUrl).host
                        : 'not set'
                    }
                  />
                  <EnvRow
                    label="Pinata Gateway"
                    value={env.pinataGateway || 'not set'}
                  />
                  <EnvRow
                    label="Notifications API"
                    value={env.notificationApiBase || 'same-origin'}
                  />
                  <EnvRow
                    label="VAPID Push"
                    value={env.vapidPublicKey ? 'configured' : 'not configured'}
                  />
                  <EnvRow
                    label="Groq (Chatbot)"
                    value="proxied via /api/chat"
                  />
                </div>
              </div>
            </>
          ) : null}
        </div>
      )}
    </section>
  )
}

function EnvRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-line/50 flex items-center justify-between border-b pb-2 last:border-0 last:pb-0">
      <span className="text-fg-secondary">{label}</span>
      <code className="bg-surface-muted text-fg rounded px-2 py-0.5 font-mono text-xs">
        {value}
      </code>
    </div>
  )
}
